// GraphExplorer — the native console graph explorer (replaces the agate iframe).
// Identity-first: roots on observed users → agents → resources (Users lens,
// default); Alerts lens surfaces alerting entities + their neighborhood. One
// graph model (lib/graph/model.ts) fed by live Prism sankeyData; elk lays it
// out, React Flow renders it; clicking a node focuses its neighborhood and fills
// the detail panel. Visual language matches the approved mockup on console tokens.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  useNodesState, useEdgesState, useReactFlow, Handle, Position,
  type Node as RFNode, type Edge as RFEdge, type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import ELK from "elkjs/lib/elk.bundled.js";
import {
  Bot, Server, Cpu, Wrench, Database, HardDrive, Box, SquareTerminal, User as UserIcon,
  AlertTriangle, Users as UsersIcon, Bell, RefreshCw, ArrowRight, Loader2,
  Search, Download, Save, Share2, ListFilter, X, Clock, Maximize2,
  ArrowUpRight, ArrowDownLeft, Activity, ShieldCheck, Webhook, Zap,
} from "lucide-react";
import { prismApi, type TraceEvent, type SessionTrace } from "@/lib/api/prism";
import {
  buildGraph, usersView, alertsView, sevRank,
  type Graph, type GNode, type GEdge, type Sev, type ViewLayout,
} from "@/lib/graph/model";

const NODE_W = 176, NODE_H = 94;
const elk = new ELK();
const LAYOUT_OPTS = {
  "elk.algorithm": "layered", "elk.direction": "RIGHT",
  "elk.layered.spacing.nodeNodeBetweenLayers": "150", "elk.spacing.nodeNode": "40",
  "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
  "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
};
async function layout(ids: string[], edges: GEdge[]) {
  const g = {
    id: "root", layoutOptions: LAYOUT_OPTS,
    children: ids.map((id) => ({ id, width: NODE_W, height: NODE_H })),
    edges: edges.map((e) => ({ id: e.id, sources: [e.source], targets: [e.target] })),
  };
  const res = (await elk.layout(g as never)) as { children?: Array<{ id: string; x?: number; y?: number }> };
  const pos = new Map<string, { x: number; y: number }>();
  (res.children ?? []).forEach((c) => pos.set(c.id, { x: c.x ?? 0, y: c.y ?? 0 }));
  return pos;
}

const SEV_COLOR: Record<string, string> = { crit: "#E5484D", high: "#F97316", med: "#FAAD14", low: "#0BA5EC" };
const sevColor = (s: Sev) => (s ? SEV_COLOR[s] ?? SEV_COLOR.crit : "#8792A2");
const KIND_ICON: Record<GNode["kind"], typeof Bot> = {
  user: UserIcon, runtime: Bot, agent: SquareTerminal, server: Server, llm: Cpu, tool: Wrench, host: HardDrive, data: Database, sandbox: Box,
};
const KIND_LABEL: Record<GNode["kind"], string> = {
  user: "User", runtime: "Agent runtime", agent: "Agent", server: "MCP Server", llm: "Model", tool: "Tool", host: "Host", data: "Data", sandbox: "Sandbox",
};

// Artifact status palette: green active, amber idle, red on alert.
const PRESENCE_COLOR = { active: "#16A34A", idle: "#FAAD14", offline: "#A9B2BF" } as const;

type NData = { n: GNode; selected?: boolean; dim?: boolean };
function EntityNode({ data }: NodeProps<RFNode<NData>>) {
  const { n, selected, dim } = data;
  const Icon = KIND_ICON[n.kind] ?? Bot;
  const sev = n.worst;
  const ring = sev ? sevColor(sev) : undefined;
  const round = n.kind === "user" || n.kind === "runtime" || n.kind === "agent" || n.kind === "data";
  // Status drives the node color: red on alert, else green active / amber idle,
  // else neutral (matches the approved artifact convention).
  const status = sev ? ring : n.presence && n.presence !== "offline" ? PRESENCE_COLOR[n.presence] : undefined;
  return (
    <div style={{ opacity: dim ? 0.34 : 1, filter: dim ? "saturate(.6)" : undefined, transition: ".18s" }}
      className="flex flex-col items-center gap-1.5" >
      <Handle type="target" position={Position.Left} style={{ background: "#CBD4E1", width: 6, height: 6 }} />
      <div className="relative grid place-items-center bg-surface-elev"
        style={{
          width: 64, height: 64, borderRadius: round ? "50%" : 16,
          border: `2px solid ${selected ? "#006BFF" : status ?? "#C3CCD8"}`,
          background: status ? `${status}14` : "#FFFFFF",
          boxShadow: selected ? "0 0 0 4px #E8F0FF, 0 10px 24px -6px rgba(31,31,31,.20)" : "0 1px 3px rgba(31,31,31,.10)",
        }}>
        {sev === "crit" && !dim && (
          <span className="pointer-events-none absolute inset-[-4px] animate-ping motion-reduce:hidden"
            style={{ border: `2px solid ${ring}`, borderRadius: round ? "50%" : 20 }} />
        )}
        <Icon size={30} strokeWidth={1.8} color={status ?? "#5B6472"} />
        {n.findings.length > 0 && (
          <span className="absolute -right-1.5 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 font-mono text-[11px] font-bold text-white"
            style={{ background: ring, boxShadow: "0 0 0 2px #FFF" }}>{n.findings.length}</span>
        )}
      </div>
      <div className="max-w-[150px] truncate text-center text-[13px] font-bold leading-tight text-text-primary">{n.label}</div>
      {n.sub && <div className="-mt-0.5 font-mono text-[10.5px] text-text-muted">{n.sub}</div>}
      <Handle type="source" position={Position.Right} style={{ background: "#CBD4E1", width: 6, height: 6 }} />
    </div>
  );
}
const nodeTypes = { entity: EntityNode };

function edgeColor(status: string, kind: string): string {
  if (kind === "owns" || kind === "runs") return "#DCE2EA";
  if (status === "blocked" || status === "violation") return "#E5484D";
  if (status === "active") return "#006BFF";
  if (status === "idle") return "#E0C77A";
  return "#D3DBE4";
}
function toRFEdge(e: GEdge): RFEdge {
  const c = edgeColor(e.status, e.kind);
  return {
    id: e.id, source: e.source, target: e.target,
    animated: e.status === "active",
    style: { stroke: c, strokeWidth: e.kind === "owns" || e.kind === "runs" ? 1.2 : 1.5, strokeDasharray: e.kind === "owns" || e.kind === "runs" ? "4 4" : undefined },
    data: { kind: e.kind },
  };
}

const LENSES = [
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "alerts", label: "Alerts", icon: Bell },
] as const;
type LensKey = (typeof LENSES)[number]["key"];

const FINDING_TITLE: Record<string, string> = {
  policy_violation: "Policy violation — action blocked",
  remediation: "Remediation applied",
};
export function findingTitle(alertType: string): string {
  return FINDING_TITLE[alertType] ?? alertType.charAt(0).toUpperCase() + alertType.slice(1).replace(/_/g, " ");
}
const SPARK = [2, 3, 2, 3, 3, 4, 4];
const VIEW_NOTE: Record<LensKey, string> = {
  users: "Each person, the agents they run, and the MCP servers & models those agents touch. Presence shows active vs idle — pivot on any user for everything they did in the window.",
  alerts: "Every entity with an open finding and what it can reach. Click a node for its findings, or trace into the session behind an alert.",
};
const SCOPE: Record<LensKey, string> = {
  users: "Observed users · agents & access",
  alerts: "Alerting entities · severity ≥ Medium",
};

interface FlatFinding { sev: Exclude<Sev, null>; title: string; rule: string; id: string; count: number; ownerId: string; ownerLabel: string; ownerKind: GNode["kind"]; userLabel: string }

function Inner() {
  const [graph, setGraph] = useState<Graph | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "empty" | "error">("loading");
  const [err, setErr] = useState("");
  const [lens, setLens] = useState<LensKey>("users");
  const [mode, setMode] = useState<"graph" | "findings">("graph");
  const [sel, setSel] = useState<string | null>(null);
  const [trace, setTrace] = useState<{ sessionId: string; label: string } | null>(null);
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState<RFNode<NData>>([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState<RFEdge>([]);
  const rf = useReactFlow();
  const curEdges = useRef<GEdge[]>([]);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const end = new Date();
      const start = new Date(end.getTime() - 30 * 864e5); // 30-day window — fleet data is sparse/historical
      const data = await prismApi.sankeyData(start.toISOString(), end.toISOString());
      const g = buildGraph(data);
      setGraph(g);
      setState(g.nodes.size ? "ok" : "empty");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setState("error");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!graph) return;
    const vl: ViewLayout = lens === "users" ? usersView(graph) : alertsView(graph);
    curEdges.current = vl.edges;
    let live = true;
    void (async () => {
      const pos = await layout(vl.visible, vl.edges);
      if (!live) return;
      setRfNodes(vl.visible.map((id) => ({
        id, type: "entity", position: pos.get(id) ?? { x: 0, y: 0 },
        data: { n: graph.nodes.get(id)! }, draggable: true,
      })));
      setRfEdges(vl.edges.map(toRFEdge));
      setSel(vl.focus);
      setTimeout(() => rf.fitView({ padding: 0.12, duration: 300 }), 70);
    })();
    return () => { live = false; };
  }, [graph, lens, rf, setRfNodes, setRfEdges]);

  useEffect(() => {
    const nb = new Set<string>(sel ? [sel] : []);
    if (sel) curEdges.current.forEach((e) => { if (e.source === sel) nb.add(e.target); if (e.target === sel) nb.add(e.source); });
    setRfNodes((ns) => ns.map((rn) => ({ ...rn, data: { ...rn.data, selected: rn.id === sel, dim: sel != null && !nb.has(rn.id) } })));
    setRfEdges((es) => es.map((e) => {
      const on = e.source === sel || e.target === sel;
      const base = curEdges.current.find((c) => c.id === e.id);
      return { ...e, style: { ...e.style, stroke: on ? "#006BFF" : edgeColor(base?.status ?? "", base?.kind ?? ""), strokeWidth: on ? 2.4 : 1.6, opacity: sel && !on ? 0.25 : 1 } };
    }));
  }, [sel, setRfNodes, setRfEdges]);

  const findings = useMemo<FlatFinding[]>(() => {
    if (!graph) return [];
    // Resolve each agent's owning user across the person → runtime hub → agent
    // chain (identified users) and the flat person → agent edge (No-user bucket).
    const userOf = new Map<string, string>();
    const directOwner = new Map<string, string>(); // hub or agent id -> user label
    graph.edges.forEach((e) => { if (e.kind === "owns") directOwner.set(e.target, graph.nodes.get(e.source)?.label ?? ""); });
    directOwner.forEach((label, id) => { if (graph.nodes.get(id)?.kind === "agent") userOf.set(id, label); });
    graph.edges.forEach((e) => { if (e.kind === "runs") { const o = directOwner.get(e.source); if (o) userOf.set(e.target, o); } });
    const out: FlatFinding[] = [];
    for (const n of graph.nodes.values())
      for (const f of n.findings)
        out.push({ ...f, ownerId: n.id, ownerLabel: n.label, ownerKind: n.kind, userLabel: userOf.get(n.id) ?? (n.kind === "user" ? n.label : "—") });
    return out.sort((a, b) => sevRank(b.sev) - sevRank(a.sev));
  }, [graph]);

  const summary = useMemo(() => {
    if (!graph) return null;
    let crit = 0, high = 0, agents = 0, active = 0, idle = 0, blocked = 0;
    for (const n of graph.nodes.values()) {
      if (n.kind === "agent") { agents++; if (n.presence === "active") active++; else if (n.presence === "idle") idle++; }
      for (const f of n.findings) {
        if (f.sev === "crit") crit++; else if (f.sev === "high") high++;
        if (/policy_violation|block|remediation/.test(f.alertType)) blocked += f.count;
      }
    }
    return { crit, high, agents, active, idle, blocked, users: graph.userIds.length };
  }, [graph]);

  const selNode = sel && graph ? graph.nodes.get(sel) : null;
  const jump = (id: string) => { setMode("graph"); if (graph?.nodes.has(id)) setSel(id); };

  return (
    <div className="flex h-full flex-col">
      {/* top bar */}
      <div className="flex items-center gap-3 border-b border-border bg-surface-elev px-4 py-2.5">
        <div className="flex items-center gap-2 font-serif text-[15px] font-bold text-text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-success ring-4 ring-success/15" /> Security Graph
        </div>
        <div className="flex rounded-lg border border-border bg-surface p-0.5">
          {LENSES.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setLens(key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors ${lens === key ? "bg-surface-elev text-text-primary shadow-sm" : "text-text-secondary hover:text-text-primary"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-raised px-2.5 py-1.5 text-[13px] font-semibold text-text-secondary">
          <ArrowRight size={13} className="rotate-90 opacity-0" /><span className="text-text-primary">Live fleet</span> · Production
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden items-center gap-2 rounded-lg border border-border bg-surface-raised px-3 text-[13px] text-text-muted lg:flex" style={{ height: 34, width: 220 }}>
            <Search size={15} /> Search entities…
          </div>
          <button className="flex h-[34px] items-center gap-1.5 rounded-lg border border-border bg-surface-raised px-3 text-[13px] font-semibold text-text-primary hover:bg-surface-raised"><Download size={15} /> Export</button>
          <button className="flex h-[34px] items-center gap-1.5 rounded-lg bg-accent px-3 text-[13px] font-semibold text-white hover:bg-accent-hover"><Save size={15} /> Save view</button>
        </div>
      </div>

      {/* query row */}
      <div className="flex flex-wrap items-center gap-2.5 border-b border-border bg-surface-elev px-4 py-2.5">
        <div className="flex rounded-lg border border-border bg-surface-raised p-0.5">
          {(["graph", "findings"] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)}
              className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[13px] font-semibold capitalize transition-colors ${mode === m ? "bg-surface-elev text-text-primary shadow-sm" : "text-text-secondary hover:text-text-primary"}`}>
              {m === "graph" ? <Share2 size={14} /> : <ListFilter size={14} />} {m}
              {m === "findings" && <span className="font-mono text-[11px] text-text-muted">{findings.length}</span>}
            </button>
          ))}
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Query</span>
        <span className="flex items-center gap-1.5 rounded-lg border border-border bg-surface-raised px-2.5 py-1.5 text-[12.5px] font-semibold text-text-secondary">{SCOPE[lens]} <X size={13} className="text-text-muted" /></span>
        <span className="flex items-center gap-1.5 rounded-lg border border-border bg-surface-raised px-2.5 py-1.5 text-[12.5px] font-semibold text-text-secondary"><Clock size={13} /> Last 30 days <X size={13} className="text-text-muted" /></span>
        <span className="flex items-center gap-1.5 rounded-lg border border-dashed border-border bg-surface-raised px-2.5 py-1.5 text-[12.5px] font-semibold text-text-secondary">+ Add filter</span>
        <button onClick={() => void load()} className="text-[12.5px] font-semibold text-accent">Reset</button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => rf.fitView({ padding: 0.12, duration: 300 })} title="Fit to view" className="grid h-[34px] w-[34px] place-items-center rounded-lg border border-border bg-surface-raised text-text-secondary hover:bg-surface-raised"><Maximize2 size={15} /></button>
          <button onClick={() => void load()} title="Refresh" className="grid h-[34px] w-[34px] place-items-center rounded-lg border border-border bg-surface-raised text-text-secondary hover:bg-surface-raised"><RefreshCw size={15} className={state === "loading" ? "animate-spin" : ""} /></button>
        </div>
      </div>

      {/* summary strip */}
      {summary && (
        <div className="grid grid-cols-5 gap-px border-b border-border bg-border">
          {([
            ["Critical findings", summary.crit, "crit", "#E5484D", summary.crit ? "needs review" : "clear"],
            ["High findings", summary.high, "high", "#F97316", summary.high ? `${summary.high} reachable` : "clear"],
            ["Blocked actions", summary.blocked, "block", "#E5484D", summary.blocked ? "policy enforced" : "none"],
            ["Agents", summary.agents, "ok", "#16A34A", `${summary.active} active · ${summary.idle} idle`],
            ["Observed users", summary.users, "info", "#0BA5EC", `${graph?.userIds.filter((u) => graph.nodes.get(u)?.label !== "No user").length ?? 0} identified`],
          ] as const).map(([label, val, , color, sub]) => (
            <div key={label} className="bg-surface-elev px-4 py-2.5">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                <span className="h-1.5 w-1.5 rounded-sm" style={{ background: color }} /> {label}
              </div>
              <div className="text-[22px] font-bold tabular-nums leading-tight text-text-primary">{val}
                {sub && <span className="ml-2 text-[12px] font-semibold text-text-muted">{sub}</span>}</div>
              <div className="mt-1 flex h-1.5 gap-0.5">
                {SPARK.map((h, i) => <span key={i} className="flex-1 rounded-sm" style={{ background: i === SPARK.length - 1 ? color : "#CBD2DA", opacity: 0.35 + (h / 5) * 0.65 }} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* stage: graph + panel, OR findings table */}
      {mode === "findings" ? (
        <FindingsTable findings={findings} onPick={jump} />
      ) : (
        <div className="grid min-h-0 flex-1" style={{ gridTemplateColumns: "1fr 342px" }}>
          <div className="relative bg-surface">
            {state === "loading" && <Center><Loader2 className="animate-spin text-accent" size={26} /><p className="mt-3 text-sm text-text-secondary">Loading graph…</p></Center>}
            {state === "error" && <Center><AlertTriangle className="text-danger" size={26} /><p className="mt-3 text-sm font-semibold text-text-primary">Couldn’t load the graph</p><p className="mt-1 max-w-sm text-center text-xs text-text-muted">{err}</p><button onClick={() => void load()} className="mt-4 rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-white">Retry</button></Center>}
            {state === "empty" && <Center><UsersIcon className="text-text-muted" size={26} /><p className="mt-3 text-sm text-text-secondary">No agent activity in this window.</p></Center>}
            {state === "ok" && (
              <>
                <div className="pointer-events-none absolute left-4 top-3 z-10 max-w-[360px] rounded-lg border border-border bg-surface-elev px-3 py-2 text-[12px] leading-snug text-text-muted shadow-sm">
                  <b className="text-text-secondary">{lens === "users" ? "Users" : "Alerts"}</b> — {VIEW_NOTE[lens]}
                </div>
                <div className="absolute bottom-3.5 left-4 z-10 flex flex-col gap-1.5 rounded-lg border border-border bg-surface-elev p-2.5 text-[11.5px] shadow-sm">
                  {(lens === "users"
                    ? ([["#16A34A", "Active", true], ["#FAAD14", "Idle", true], ["#E5484D", "Has alert", true]] as const)
                    : ([["#E5484D", "Critical finding", false], ["#F97316", "High finding", false], ["#FAAD14", "Medium finding", false]] as const)
                  ).map(([c, l, round], i) => (
                    <div key={i} className="flex items-center gap-2 font-semibold text-text-secondary">
                      <span className="h-2.5 w-2.5" style={{ background: c, borderRadius: round ? "50%" : 3 }} /> {l}
                    </div>
                  ))}
                </div>
                <ReactFlow nodes={rfNodes} edges={rfEdges} nodeTypes={nodeTypes}
                  onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                  onNodeClick={(_, node) => setSel(node.id)} onPaneClick={() => setSel(null)}
                  fitView minZoom={0.55} proOptions={{ hideAttribution: true }} className="bg-surface">
                  <Background color="#DDE2E6" gap={22} />
                  <Controls showInteractive={false} />
                  <MiniMap pannable zoomable nodeStrokeWidth={2}
                    nodeColor={(nd) => { const n = (nd.data as NData).n; return n.worst ? sevColor(n.worst) : "#CBD2DA"; }}
                    style={{ background: "#FFFFFF", border: "1px solid #DDE2E6" }} />
                </ReactFlow>
              </>
            )}
          </div>
          <aside className="flex min-h-0 flex-col border-l border-border bg-surface-elev">
            {selNode ? <Detail n={selNode} graph={graph!} onPick={setSel}
                onTrace={() => setMode("findings")}
                onOpenTrace={(sessionId, label) => setTrace({ sessionId, label })}
                onFocus={() => { if (sel) rf.fitView({ nodes: [{ id: sel }], padding: 0.5, duration: 400 }); }} /> :
              <div className="grid flex-1 place-items-center p-8 text-center text-sm text-text-muted">Select an entity to see its detail.</div>}
          </aside>
        </div>
      )}
      {trace && <TraceDrawer sessionId={trace.sessionId} label={trace.label} onClose={() => setTrace(null)} />}
    </div>
  );
}

function FindingsTable({ findings, onPick }: { findings: FlatFinding[]; onPick: (id: string) => void }) {
  const KI = KIND_ICON;
  return (
    <div className="min-h-0 flex-1 overflow-auto bg-surface-elev">
      {findings.length === 0 ? (
        <div className="grid h-full place-items-center text-sm text-text-muted">No open findings in this window.</div>
      ) : (
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>{["Severity", "Finding", "Entity", "User", "Rule", "Alert", "Count"].map((h) => (
              <th key={h} className="sticky top-0 z-10 border-b border-border bg-surface-elev px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-text-muted">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {findings.map((f, i) => { const RI = KI[f.ownerKind] ?? Bot; return (
              <tr key={i} onClick={() => onPick(f.ownerId)} className="cursor-pointer border-b border-border hover:bg-surface-raised">
                <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1.5 font-bold" style={{ color: sevColor(f.sev) }}><span className="h-2 w-2 rounded-full" style={{ background: sevColor(f.sev) }} />{{ crit: "Critical", high: "High", med: "Medium", low: "Low" }[f.sev]}</span></td>
                <td className="px-4 py-2.5 font-semibold text-text-primary">{findingTitle(f.rule)}</td>
                <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1.5 text-text-primary"><RI size={13} className="text-text-muted" /> {f.ownerLabel}</span></td>
                <td className="px-4 py-2.5 text-text-secondary">{f.userLabel}</td>
                <td className="px-4 py-2.5 font-mono text-[12px] text-text-muted">{f.rule}</td>
                <td className="px-4 py-2.5 font-mono text-[12px] text-text-muted">{f.id}</td>
                <td className="px-4 py-2.5 font-mono tabular-nums text-text-secondary">{f.count}</td>
              </tr>
            ); })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>;
}

function SevPill({ sev }: { sev: Sev }) {
  if (!sev) return <span className="rounded-md bg-success/10 px-2 py-1 text-[11px] font-bold uppercase text-success">Healthy</span>;
  const label = { crit: "Critical", high: "High", med: "Medium", low: "Low" }[sev];
  const c = sevColor(sev);
  return <span className="rounded-md px-2 py-1 text-[11px] font-bold uppercase" style={{ background: `${c}18`, color: c }}>{label}</span>;
}

function Detail({ n, graph, onPick, onTrace, onOpenTrace, onFocus }: { n: GNode; graph: Graph; onPick: (id: string) => void; onTrace: () => void; onOpenTrace: (sessionId: string, label: string) => void; onFocus: () => void }) {
  const Icon = KIND_ICON[n.kind] ?? Bot;
  const rel = new Set<string>();
  // connected in the *whole* graph (both identity + activity edges)
  graph.edges.forEach((e) => { if (e.source === n.id) rel.add(e.target); if (e.target === n.id) rel.add(e.source); });
  return (
    <>
      <div className="border-b border-border px-4 pb-3.5 pt-4">
        <div className="flex items-center gap-2">
          <SevPill sev={n.worst} />
          {n.presence && <span className="rounded-md px-2 py-1 text-[11px] font-bold uppercase" style={{ background: n.presence === "active" ? "#16A34A18" : "#FAAD1418", color: n.presence === "active" ? "#16A34A" : "#B8860B" }}>{n.presence}</span>}
        </div>
        <h2 className="mt-2.5 font-serif text-[19px] font-bold leading-tight text-text-primary">{n.label}</h2>
        <div className="mt-0.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-text-secondary">
          <Icon size={15} /> {KIND_LABEL[n.kind]}{n.sub && <span className="font-mono text-[12px] text-text-muted">· {n.sub}</span>}
        </div>
      </div>
      <div className="flex flex-col gap-5 overflow-y-auto px-4 py-4">
        {n.findings.length > 0 && (
          <section>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-text-muted">Findings · {n.findings.length}</div>
            {n.findings.map((f) => (
              <div key={f.id} className="mb-2 flex gap-2.5 rounded-lg border border-border bg-surface-raised p-2.5">
                <span className="w-[3px] shrink-0 rounded" style={{ background: sevColor(f.sev) }} />
                <div className="min-w-0">
                  <div className="text-[13px] font-bold leading-snug text-text-primary">{findingTitle(f.rule)}</div>
                  {f.message && <div className="mt-1 line-clamp-4 break-words text-[12px] leading-snug text-text-secondary">{f.message}</div>}
                  <div className="mt-1.5 flex flex-wrap gap-x-2.5 gap-y-1 font-mono text-[11px] text-text-muted"><span>{f.rule}</span><span>{f.id}</span>{f.count > 1 && <span>&times;{f.count}</span>}</div>
                </div>
              </div>
            ))}
          </section>
        )}
        <section>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-text-muted">{n.kind === "user" ? "Identity" : "Properties"}</div>
          <dl className="grid grid-cols-[92px_1fr] gap-x-3 gap-y-2 text-[13px]">
            {Object.entries(n.fields).map(([k, v]) => (
              <div key={k} className="contents"><dt className="font-semibold text-text-muted">{k}</dt>
                <dd className={`font-semibold text-text-primary ${/host|id/i.test(k) ? "font-mono text-[12px]" : ""}`}>{v}</dd></div>
            ))}
          </dl>
        </section>
        {rel.size > 0 && (
          <section>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-text-muted">Connected · {rel.size}</div>
            {[...rel].map((rid) => { const r = graph.nodes.get(rid); if (!r) return null; const RI = KIND_ICON[r.kind] ?? Bot;
              return (
                <button key={rid} onClick={() => onPick(rid)} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-surface-raised">
                  <span className="grid h-6 w-6 place-items-center rounded-md bg-surface-raised text-text-secondary"><RI size={14} /></span>
                  <span className="text-[12.5px] font-semibold text-text-primary">{r.label}</span>
                  <span className="ml-auto font-mono text-[11px] text-text-muted">{KIND_LABEL[r.kind]}</span>
                </button>
              );
            })}
          </section>
        )}
      </div>
      <div className="flex gap-2 border-t border-border px-4 py-3">
        {n.sessionId ? (
          <button onClick={() => onOpenTrace(n.sessionId!, n.label)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface py-2 text-[13px] font-semibold text-text-primary hover:bg-surface-raised"><Activity size={14} /> Trace session</button>
        ) : (
          <button onClick={onTrace} className="flex-1 rounded-lg border border-border bg-surface py-2 text-[13px] font-semibold text-text-primary hover:bg-surface-raised">{n.kind === "user" ? "View activity" : "Trace"}</button>
        )}
        <button onClick={onFocus} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-accent py-2 text-[13px] font-semibold text-white hover:bg-accent-hover">Focus <Maximize2 size={14} /></button>
      </div>
    </>
  );
}

// ── Session trace ladder (Phase 2.5) ────────────────────────────────────────
// A drawer over the stage: the ordered event timeline for one session, as an
// interactions ladder (# / DIR / TIME / SOURCE → DEST / EVENT / STATUS / MS)
// with category filter chips and a per-event payload drilldown. Mirrors agate's
// `... | trace` view but keyed off a node's session rather than a GFL query.

type TGroup = "llm" | "mcp" | "tool" | "hook" | "permission" | "brace" | "other";
const TGROUP: Record<TGroup, { label: string; color: string; icon: typeof Cpu }> = {
  llm: { label: "LLM", color: "#7C5CFF", icon: Cpu },
  mcp: { label: "MCP", color: "#006BFF", icon: Server },
  tool: { label: "Tool", color: "#0BA5EC", icon: Wrench },
  hook: { label: "Hook", color: "#16A34A", icon: Webhook },
  permission: { label: "Permission", color: "#F97316", icon: ShieldCheck },
  brace: { label: "Kernel", color: "#8792A2", icon: Zap },
  other: { label: "Other", color: "#A9B2BF", icon: Activity },
};
// Classify an event into a filter group from its category / name. Prism's
// `category` is authoritative when present; name substrings backstop it.
function groupOf(ev: TraceEvent): TGroup {
  const c = (ev.category ?? "").toLowerCase();
  const nm = `${ev.eventName ?? ""} ${ev.method ?? ""}`.toLowerCase();
  if (c.includes("llm") || /llm|completion|message|inference|token/.test(nm)) return "llm";
  if (ev.mcpEvent || c.includes("mcp") || /mcp/.test(nm)) return "mcp";
  if (ev.toolName || c.includes("tool") || /tool_(call|use|result)/.test(nm)) return "tool";
  if (c.includes("hook") || /hook/.test(nm)) return "hook";
  if (c.includes("permission") || /permission|approval|consent/.test(nm)) return "permission";
  if (c.includes("brace") || /brace|kernel|syscall|execve|connect|dns/.test(nm)) return "brace";
  return "other";
}
// Outbound = leaves the agent (LLM/MCP/tool/kernel egress); inbound/internal =
// hooks, permission changes, everything else.
const OUTBOUND: Record<TGroup, boolean> = { llm: true, mcp: true, tool: true, brace: true, hook: false, permission: false, other: false };

const SEV_TEXT: Record<string, { label: string; color: string }> = {
  CRITICAL: { label: "Critical", color: "#E5484D" }, HIGH: { label: "High", color: "#F97316" },
  MEDIUM: { label: "Medium", color: "#FAAD14" }, LOW: { label: "Low", color: "#0BA5EC" },
  WARNING: { label: "Warning", color: "#FAAD14" }, ERROR: { label: "Error", color: "#E5484D" },
  INFO: { label: "OK", color: "#16A34A" }, DEBUG: { label: "Debug", color: "#8792A2" },
};
function sevText(s?: string | null) { return (s && SEV_TEXT[s.toUpperCase()]) || { label: s || "—", color: "#8792A2" }; }
function fmtTime(ts: string): string {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) +
    "." + String(d.getMilliseconds()).padStart(3, "0");
}
const eventName = (ev: TraceEvent) => ev.method || ev.eventName || ev.toolName || "event";
const destOf = (ev: TraceEvent) => ev.toolName || (ev.mcpEvent ? "mcp server" : ev.method || ev.hostShortName || "—");
function prettyPayload(p?: string | null): string {
  if (!p) return "";
  try { return JSON.stringify(JSON.parse(p), null, 2); } catch { return p; }
}
// The assistant's reply text for a Stop/turn event. CCPD (Stage 10) puts it in the
// event's LogAttributes as `last_assistant_message`, which the trace returns inside
// `payload` — surface it as first-class prose instead of leaving it buried in JSON.
function assistantReply(ev: TraceEvent): string | null {
  const p = ev.payload;
  if (!p || !p.includes("last_assistant_message")) return null;
  try {
    const o = JSON.parse(p) as Record<string, unknown>;
    const v = o["last_assistant_message"];
    return typeof v === "string" && v.trim() ? v : null;
  } catch { return null; }
}

function TraceDrawer({ sessionId, label, onClose }: { sessionId: string; label: string; onClose: () => void }) {
  const [data, setData] = useState<SessionTrace | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [err, setErr] = useState("");
  const [off, setOff] = useState<Set<TGroup>>(new Set()); // groups toggled OFF
  const [pick, setPick] = useState<string | null>(null); // selected eventId

  useEffect(() => {
    let live = true;
    setState("loading");
    prismApi.sessionTrace(sessionId).then((d) => {
      if (!live) return;
      setData(d); setState("ok");
    }).catch((e) => { if (!live) return; setErr(e instanceof Error ? e.message : String(e)); setState("error"); });
    return () => { live = false; };
  }, [sessionId]);

  // Esc closes the drawer.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const events = data?.events ?? [];
  const counts = useMemo(() => {
    const m = new Map<TGroup, number>();
    events.forEach((ev) => { const g = groupOf(ev); m.set(g, (m.get(g) ?? 0) + 1); });
    return m;
  }, [events]);
  const groups = useMemo(() => (Object.keys(TGROUP) as TGroup[]).filter((g) => (counts.get(g) ?? 0) > 0), [counts]);
  const rows = useMemo(() => events.filter((ev) => !off.has(groupOf(ev))), [events, off]);
  const picked = pick ? events.find((e) => e.eventId === pick) ?? null : null;

  const toggle = (g: TGroup) => setOff((s) => { const n = new Set(s); n.has(g) ? n.delete(g) : n.add(g); return n; });

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-black/25 backdrop-blur-[1px]" onClick={onClose}>
      <div className="mx-auto mt-8 flex h-[calc(100%-3rem)] w-[min(1180px,96%)] flex-col overflow-hidden rounded-xl border border-border bg-surface-elev shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent/10 text-accent"><Activity size={17} /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-serif text-[15px] font-bold leading-tight text-text-primary">
              Session trace <span className="truncate font-sans text-[13px] font-semibold text-text-secondary">· {label}</span>
            </div>
            <div className="font-mono text-[11px] text-text-muted">{sessionId}{data && <span className="ml-2">· {data.totalEvents} events{data.truncated && " (truncated)"}</span>}</div>
          </div>
          <button onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-raised"><X size={16} /></button>
        </div>

        {/* filter chips */}
        {state === "ok" && groups.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-4 py-2.5">
            <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Categories</span>
            {groups.map((g) => { const meta = TGROUP[g]; const Ic = meta.icon; const on = !off.has(g);
              return (
                <button key={g} onClick={() => toggle(g)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${on ? "border-transparent text-white" : "border-border bg-surface text-text-muted"}`}
                  style={on ? { background: meta.color } : undefined}>
                  <Ic size={12} /> {meta.label} <span className="font-mono opacity-80">{counts.get(g)}</span>
                </button>
              );
            })}
            <button onClick={() => setOff(new Set())} className="ml-1 text-[12px] font-semibold text-accent">All</button>
          </div>
        )}

        {/* body: ladder + detail */}
        <div className="grid min-h-0 flex-1" style={{ gridTemplateColumns: picked ? "1fr 400px" : "1fr" }}>
          <div className="min-h-0 overflow-auto">
            {state === "loading" && <div className="grid h-full place-items-center"><Loader2 className="animate-spin text-accent" size={24} /></div>}
            {state === "error" && <div className="grid h-full place-items-center px-8 text-center"><div><AlertTriangle className="mx-auto text-danger" size={22} /><p className="mt-2 text-sm font-semibold text-text-primary">Couldn’t load the trace</p><p className="mt-1 text-xs text-text-muted">{err}</p></div></div>}
            {state === "ok" && rows.length === 0 && <div className="grid h-full place-items-center text-sm text-text-muted">{events.length ? "No events match the active filters." : "No events recorded for this session."}</div>}
            {state === "ok" && rows.length > 0 && (
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr>{["#", "", "Time", "Source", "", "Dest", "Event", "Status", "ms"].map((h, i) => (
                    <th key={i} className="sticky top-0 z-10 border-b border-border bg-surface-elev px-3 py-2 text-left text-[10.5px] font-bold uppercase tracking-wide text-text-muted">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {rows.map((ev, i) => { const g = groupOf(ev); const meta = TGROUP[g]; const out = OUTBOUND[g]; const sv = sevText(ev.severity);
                    const on = ev.eventId === pick;
                    return (
                      <tr key={ev.eventId} onClick={() => setPick(on ? null : ev.eventId)}
                        className={`cursor-pointer border-b border-border/70 hover:bg-surface-raised ${on ? "bg-accent/5" : ""}`}>
                        <td className="px-3 py-1.5 font-mono text-[11px] tabular-nums text-text-muted">{i + 1}</td>
                        <td className="px-1 py-1.5">{out ? <ArrowUpRight size={14} className="text-accent" /> : <ArrowDownLeft size={14} className="text-text-muted" />}</td>
                        <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[11.5px] tabular-nums text-text-secondary">{fmtTime(ev.timestamp)}</td>
                        <td className="max-w-[150px] truncate px-3 py-1.5 text-text-primary">{ev.entityShortName || ev.hostShortName || label}</td>
                        <td className="px-1 py-1.5 text-text-muted"><ArrowRight size={12} /></td>
                        <td className="max-w-[150px] truncate px-3 py-1.5 text-text-secondary">{destOf(ev)}</td>
                        <td className="px-3 py-1.5">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="grid h-4 w-4 place-items-center rounded" style={{ background: `${meta.color}1A`, color: meta.color }}><meta.icon size={11} /></span>
                            <span className="font-semibold text-text-primary">{eventName(ev)}</span>
                          </span>
                        </td>
                        <td className="px-3 py-1.5"><span className="inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: sv.color }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: sv.color }} />{sv.label}</span></td>
                        <td className="px-3 py-1.5 text-right font-mono text-[11.5px] tabular-nums text-text-secondary">{ev.durationMs != null ? ev.durationMs : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* event detail drilldown */}
          {picked && (
            <div className="flex min-h-0 flex-col border-l border-border bg-surface">
              <div className="flex items-start gap-2 border-b border-border px-4 py-3">
                <div className="min-w-0">
                  <div className="text-[10.5px] font-bold uppercase tracking-wide text-text-muted">{TGROUP[groupOf(picked)].label} event</div>
                  <div className="break-words font-serif text-[15px] font-bold leading-tight text-text-primary">{eventName(picked)}</div>
                </div>
                <button onClick={() => setPick(null)} className="ml-auto grid h-6 w-6 shrink-0 place-items-center rounded-md text-text-muted hover:bg-surface-raised"><X size={14} /></button>
              </div>
              <div className="flex-1 overflow-auto px-4 py-3">
                <dl className="grid grid-cols-[78px_1fr] gap-x-3 gap-y-1.5 text-[12.5px]">
                  {([
                    ["Time", fmtTime(picked.timestamp)], ["Category", picked.category || "—"],
                    ["Severity", sevText(picked.severity).label], ["Method", picked.method || "—"],
                    ["Tool", picked.toolName || "—"], ["Duration", picked.durationMs != null ? `${picked.durationMs} ms` : "—"],
                    ["Entity", picked.entityShortName || "—"], ["Host", picked.hostShortName || "—"],
                    ["Span", picked.spanId || "—"],
                  ] as const).filter(([, v]) => v && v !== "—").map(([k, v]) => (
                    <div key={k} className="contents"><dt className="font-semibold text-text-muted">{k}</dt>
                      <dd className={`break-words font-semibold text-text-primary ${/span|host/i.test(k) ? "font-mono text-[11.5px]" : ""}`}>{v}</dd></div>
                  ))}
                </dl>
                {picked.summary && <p className="mt-3 rounded-lg border border-border bg-surface-elev p-2.5 text-[12.5px] leading-snug text-text-secondary">{picked.summary}</p>}
                {assistantReply(picked) && (
                  <>
                    <div className="mt-3 text-[10.5px] font-bold uppercase tracking-wide text-text-muted">Assistant reply</div>
                    <p className="mt-1.5 max-h-[30vh] overflow-auto whitespace-pre-wrap rounded-lg border border-accent/30 bg-accent/5 p-2.5 text-[12.5px] leading-relaxed text-text-primary">{assistantReply(picked)}</p>
                  </>
                )}
                <div className="mt-3 text-[10.5px] font-bold uppercase tracking-wide text-text-muted">Arguments</div>
                <pre className="mt-1.5 max-h-[46vh] overflow-auto rounded-lg border border-border bg-[#0F1729] p-3 font-mono text-[11.5px] leading-relaxed text-[#D6DEEB]">{prettyPayload(picked.payload) || "— no payload —"}</pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GraphExplorer() {
  return <ReactFlowProvider><Inner /></ReactFlowProvider>;
}
